// סרביס מרכזי לכל קריאות ה-API
class ApiService {

  // --- 1. מזג אוויר (Open-Meteo) ---
  // הוחלף מ-wttr.in ל-Open-Meteo ליציבות מירבית
  async fetchWeather(coordinates, cityName) {
    try {
      if (!coordinates || !coordinates.lat || !coordinates.lng) {
        throw new Error('חסרים קואורדינטות לארגון');
      }

      const { lat, lng } = coordinates;

      // הגדרת URL ל-Open-Meteo
      // מבקשים: טמפרטורה, לחות, טמפרטורה מורגשת, קוד מזג אוויר, ומהירות רוח
      const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current=temperature_2m,relative_humidity_2m,apparent_temperature,weather_code,wind_speed_10m,is_day&timezone=auto`;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 שניות מספיקות בהחלט

      const response = await fetch(url, { signal: controller.signal });
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Weather API status: ${response.status}`);
      }

      const data = await response.json();
      const current = data.current;

      // בדיקה אם יום או לילה (1 = יום, 0 = לילה)
      const isDay = current.is_day === 1;

      // המרת קוד מזג האוויר (WMO code) לטקסט ואייקון
      const weatherInfo = this.getWeatherFromWmoCode(current.weather_code, isDay);

      // החזרת המבנה בדיוק כמו שהאפליקציה שלך רגילה לקבל
      return {
        name: cityName || 'מיקום נוכחי',
        main: {
          temp: Math.round(current.temperature_2m),
          humidity: Math.round(current.relative_humidity_2m),
          feels_like: Math.round(current.apparent_temperature)
        },
        weather: [{
          description: weatherInfo.description,
          icon: weatherInfo.icon
        }],
        wind: { speed: current.wind_speed_10m } // OpenMeteo נותן קמ"ש כברירת מחדל
      };
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('Weather API request timed out');
      }
      console.error('Weather API Error:', error);
      throw error;
    }
  }

  // --- פונקציית עזר חדשה: תרגום קודי WMO (של OpenMeteo) ---
  getWeatherFromWmoCode(code, isDay = true) {
    // מבוסס על טבלת WMO Weather interpretation codes (WW)
    // https://open-meteo.com/en/docs

    // בחירת סיומת יום/לילה
    const daySuffix = isDay ? 'd' : 'n';

    const codes = {
      0: { description: 'שמיים בהירים', icon: `01${daySuffix}` },
      1: { description: 'בהיר ברובו', icon: `01${daySuffix}` },
      2: { description: 'מעונן חלקית', icon: `02${daySuffix}` },
      3: { description: 'מעונן', icon: `03${daySuffix}` },
      45: { description: 'ערפל', icon: `50${daySuffix}` },
      48: { description: 'ערפל כפור', icon: `50${daySuffix}` },
      51: { description: 'טפטוף קל', icon: `09${daySuffix}` },
      53: { description: 'טפטוף בינוני', icon: `09${daySuffix}` },
      55: { description: 'טפטוף כבד', icon: `09${daySuffix}` },
      56: { description: 'טפטוף קפוא קל', icon: `09${daySuffix}` },
      57: { description: 'טפטוף קפוא', icon: `09${daySuffix}` },
      61: { description: 'גשם קל', icon: `10${daySuffix}` },
      63: { description: 'גשם', icon: `10${daySuffix}` },
      65: { description: 'גשם כבד', icon: `10${daySuffix}` },
      66: { description: 'גשם קפוא קל', icon: `10${daySuffix}` },
      67: { description: 'גשם קפוא', icon: `10${daySuffix}` },
      71: { description: 'שלג קל', icon: `13${daySuffix}` },
      73: { description: 'שלג', icon: `13${daySuffix}` },
      75: { description: 'שלג כבד', icon: `13${daySuffix}` },
      77: { description: 'גרגירי שלג', icon: `13${daySuffix}` },
      80: { description: 'ממטרי גשם קלים', icon: `09${daySuffix}` },
      81: { description: 'ממטרי גשם', icon: `09${daySuffix}` },
      82: { description: 'ממטרי גשם עזים', icon: `09${daySuffix}` },
      85: { description: 'ממטרי שלג קלים', icon: `13${daySuffix}` },
      86: { description: 'ממטרי שלג', icon: `13${daySuffix}` },
      95: { description: 'סופת רעמים', icon: `11${daySuffix}` },
      96: { description: 'סופת רעמים וברד קל', icon: `11${daySuffix}` },
      99: { description: 'סופת רעמים וברד כבד', icon: `11${daySuffix}` }
    };

    // ברירת מחדל אם הקוד לא נמצא
    return codes[code] || { description: 'מזג אוויר משתנה', icon: `02${daySuffix}` };
  }

  // --- helper: tzid דינמי לפי מיקום ---
  _getTzid(location) {
    return location?.timezone || 'Asia/Jerusalem';
  }

  // --- 2. זמנים יומיים רגילים (Daily Zmanim) ---
  async fetchZmanim(coordinates) {
    try {
      // coordinates חייב להיות אובייקט {lat, lng}
      if (!coordinates || !coordinates.lat || !coordinates.lng) {
        throw new Error('חסרים קואורדינטות לארגון. אנא צור ארגון מחדש.');
      }

      const { lat, lng } = coordinates;
      const tzid = this._getTzid(coordinates);

      // חישוב תאריכים - היום ומחר
      const now = new Date();
      const today = this.formatDateForApi(now);
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = this.formatDateForApi(tomorrow);

      // משיכת זמנים ל-2 ימים במקביל
      const [todayRes, tomorrowRes] = await Promise.all([
        fetch(`https://www.hebcal.com/zmanim?cfg=json&geo=pos&sec=1&latitude=${lat}&longitude=${lng}&tzid=${tzid}&date=${today}`),
        fetch(`https://www.hebcal.com/zmanim?cfg=json&geo=pos&sec=1&latitude=${lat}&longitude=${lng}&tzid=${tzid}&date=${tomorrowStr}`)
      ]);

      if (!todayRes.ok || !tomorrowRes.ok) throw new Error('Zmanim API failed');

      const todayData = await todayRes.json();
      const tomorrowData = await tomorrowRes.json();

      // פורמט זמני היום
      const formatted = {};
      for (const [key, value] of Object.entries(todayData.times)) {
        formatted[key] = this.formatTime(value);
      }

      // שמירת גרסת 16.1° (72 דק׳) של מג"א לפני ה-override — ה-API מחזיר sofZmanShmaMGA כ-16.1° כברירת מחדל
      if (!formatted.sofZmanShmaMGA16Point1  && formatted.sofZmanShmaMGA)   formatted.sofZmanShmaMGA16Point1  = formatted.sofZmanShmaMGA;
      if (!formatted.sofZmanTfillaMGA16Point1 && formatted.sofZmanTfillaMGA) formatted.sofZmanTfillaMGA16Point1 = formatted.sofZmanTfillaMGA;

      // שימוש בגרסת 19.8° למג"א — המחמירה יותר (עלות מוקדם יותר → שעה הלכתית מוקדמת יותר)
      if (formatted.sofZmanShmaMGA19Point8)   formatted.sofZmanShmaMGA   = formatted.sofZmanShmaMGA19Point8;
      if (formatted.sofZmanTfillaMGA19Point8) formatted.sofZmanTfillaMGA = formatted.sofZmanTfillaMGA19Point8;
      // נץ החמה — עם שניות
      if (todayData.times.sunrise) formatted.sunrise = this.formatTimeWithSeconds(todayData.times.sunrise);

      // ─── חישוב זמני ערב פסח (היום) ───────────────────────────────────────────
      // סוף זמן אכילת חמץ (גר"א) = שעה 4 = זהה לסוף זמן תפילה (גר"א)
      if (!formatted.sofZmanAchilatChametz) {
        formatted.sofZmanAchilatChametz = formatted.sofZmanTfilla;
      }
      // סוף זמן ביעור חמץ (גר"א) = שעה 5 = אמצע בין סוף תפילה לחצות
      if (todayData.times.sofZmanTfilla && todayData.times.chatzot) {
        const biurMs = (new Date(todayData.times.sofZmanTfilla).getTime() + new Date(todayData.times.chatzot).getTime()) / 2;
        if (!formatted.sofZmanBiurChametz) formatted.sofZmanBiurChametz = this.formatTime(new Date(biurMs));
      }
      // סוף זמן אכילת חמץ (מג"א) = זהה לסוף זמן תפילה (מג"א 16.1°)
      if (!formatted.sofZmanAchilatChametzMGA) {
        formatted.sofZmanAchilatChametzMGA = formatted.sofZmanTfillaMGA16Point1;
      }
      // סוף זמן ביעור חמץ (מג"א) = אמצע בין סוף תפילה מג"א לחצות
      if (todayData.times.sofZmanTfillaMGA16Point1 && todayData.times.chatzot) {
        const biurMgaMs = (new Date(todayData.times.sofZmanTfillaMGA16Point1).getTime() + new Date(todayData.times.chatzot).getTime()) / 2;
        if (!formatted.sofZmanBiurChametzMGA) formatted.sofZmanBiurChametzMGA = this.formatTime(new Date(biurMgaMs));
      }

      // הוספת זמני מחר עם סיומת _tomorrow
      for (const [key, value] of Object.entries(tomorrowData.times)) {
        formatted[`${key}_tomorrow`] = this.formatTime(value);
      }
      // מחר — שמירת 16.1° לפני ה-override
      if (!formatted.sofZmanShmaMGA16Point1_tomorrow  && formatted.sofZmanShmaMGA_tomorrow)   formatted.sofZmanShmaMGA16Point1_tomorrow  = formatted.sofZmanShmaMGA_tomorrow;
      if (!formatted.sofZmanTfillaMGA16Point1_tomorrow && formatted.sofZmanTfillaMGA_tomorrow) formatted.sofZmanTfillaMGA16Point1_tomorrow = formatted.sofZmanTfillaMGA_tomorrow;

      // מחר — override מג"א ל-19.8° גם
      if (formatted.sofZmanShmaMGA19Point8_tomorrow)   formatted.sofZmanShmaMGA_tomorrow   = formatted.sofZmanShmaMGA19Point8_tomorrow;
      if (formatted.sofZmanTfillaMGA19Point8_tomorrow) formatted.sofZmanTfillaMGA_tomorrow = formatted.sofZmanTfillaMGA19Point8_tomorrow;
      // נץ החמה מחר — עם שניות
      if (tomorrowData.times.sunrise) formatted.sunrise_tomorrow = this.formatTimeWithSeconds(tomorrowData.times.sunrise);

      // ─── חישוב זמני ערב פסח (מחר) ────────────────────────────────────────────
      if (!formatted.sofZmanAchilatChametz_tomorrow) {
        formatted.sofZmanAchilatChametz_tomorrow = formatted.sofZmanTfilla_tomorrow;
      }
      if (tomorrowData.times.sofZmanTfilla && tomorrowData.times.chatzot) {
        const biurMsTomorrow = (new Date(tomorrowData.times.sofZmanTfilla).getTime() + new Date(tomorrowData.times.chatzot).getTime()) / 2;
        if (!formatted.sofZmanBiurChametz_tomorrow) formatted.sofZmanBiurChametz_tomorrow = this.formatTime(new Date(biurMsTomorrow));
      }
      if (!formatted.sofZmanAchilatChametzMGA_tomorrow) {
        formatted.sofZmanAchilatChametzMGA_tomorrow = formatted.sofZmanTfillaMGA16Point1_tomorrow;
      }
      if (tomorrowData.times.sofZmanTfillaMGA16Point1 && tomorrowData.times.chatzot) {
        const biurMgaMsTomorrow = (new Date(tomorrowData.times.sofZmanTfillaMGA16Point1).getTime() + new Date(tomorrowData.times.chatzot).getTime()) / 2;
        if (!formatted.sofZmanBiurChametzMGA_tomorrow) formatted.sofZmanBiurChametzMGA_tomorrow = this.formatTime(new Date(biurMgaMsTomorrow));
      }

      return formatted;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  // --- 3. זמני שבת (Shabbat Times) ---
  /**
   * מחזיר אובייקט עם מטא (רק פרשה) וזמנים ספציפיים לשישי ולשבת
   */
  async fetchShabbatTimes(coordinates) {
    try {
      // coordinates חייב להיות אובייקט {lat, lng}
      if (!coordinates || !coordinates.lat || !coordinates.lng) {
        throw new Error('חסרים קואורדינטות לארגון. אנא צור ארגון מחדש.');
      }

      const { lat, lng } = coordinates;
      const tzid = this._getTzid(coordinates);

      // א. חישוב תאריכים (שישי ושבת הקרובים)
      const dates = this.getWeekendDates();
      const fridayStr = this.formatDateForApi(dates.friday);
      const saturdayStr = this.formatDateForApi(dates.saturday);

      // ב. ביצוע 3 קריאות במקביל ל-Hebcal
      const [shabbatGeneralRes, fridayZmanimRes, saturdayZmanimRes] = await Promise.all([
        // 1. מידע כללי (פרשה, נרות, הבדלה) - ברירת מחדל של ה-API (18 דקות לפני שקיעה)
        fetch(`https://www.hebcal.com/shabbat?cfg=json&geo=pos&sec=1&latitude=${lat}&longitude=${lng}&tzid=${tzid}&date=${fridayStr}`),
        // 2. זמנים מדויקים ליום שישי
        fetch(`https://www.hebcal.com/zmanim?cfg=json&geo=pos&sec=1&latitude=${lat}&longitude=${lng}&tzid=${tzid}&date=${fridayStr}`),
        // 3. זמנים מדויקים ליום שבת
        fetch(`https://www.hebcal.com/zmanim?cfg=json&geo=pos&sec=1&latitude=${lat}&longitude=${lng}&tzid=${tzid}&date=${saturdayStr}`)
      ]);

      if (!shabbatGeneralRes.ok || !fridayZmanimRes.ok || !saturdayZmanimRes.ok) {
        throw new Error('Failed to fetch Shabbat data');
      }

      const generalData = await shabbatGeneralRes.json();
      const friData = await fridayZmanimRes.json();
      const satData = await saturdayZmanimRes.json();

      const friTimes = friData.times;
      const satTimes = satData.times;

      // ג. חילוץ הנתונים מהתשובה הכללית
      const candleItem = generalData.items?.find(i => i.category === 'candles');
      const havdalahItem = generalData.items?.find(i => i.category === 'havdalah');
      const parshaItem = generalData.items?.find(i => i.category === 'parashat');

      // ד. החזרת האובייקט הסופי
      return {
        meta: {
          parsha: (parshaItem?.hebrew || 'פרשה').replace(/^פרשת /, ''),
          candleLighting: this.formatTime(candleItem?.date),
          havdalah: this.formatTime(havdalahItem?.date)
        },

        friday: {
          sunset: this.formatTime(friTimes.sunset),
          tzeit7083deg: this.formatTime(friTimes.tzeit7083deg),
          tzeit85deg: this.formatTime(friTimes.tzeit85deg),
          tzeit42min: this.formatTime(friTimes.tzeit42min),
          tzeit50min: this.formatTime(friTimes.tzeit50min),
          tzeit72min: this.formatTime(friTimes.tzeit72min),
          netzHachama: this.formatTimeWithSeconds(friTimes.sunrise || friTimes.netzHachama),
          plagHamincha: this.formatTime(friTimes.plagHaMincha),
          minchaGedola: this.formatTime(friTimes.minchaGedola),
          minchaKetana: this.formatTime(friTimes.minchaKetana),
          chatzot: this.formatTime(friTimes.chatzot),
          alotHaShachar: this.formatTime(friTimes.alotHaShachar),
        },

        shabbat: {
          sofZmanShmaGra:    this.formatTime(satTimes.sofZmanShma),
          sofZmanShmaMga:    this.formatTime(satTimes.sofZmanShmaMGA),           // Hebcal returns sofZmanShmaMGA (16.1°) not sofZmanShmaMGA16Point1
          sofZmanShmaMga19:  this.formatTime(satTimes.sofZmanShmaMGA19Point8),   // 19.8° variant if present
          sofZmanTfilla:     this.formatTime(satTimes.sofZmanTfilla),
          sofZmanTfillaMGA:  this.formatTime(satTimes.sofZmanTfillaMGA),
          sunset: this.formatTime(satTimes.sunset),
          tzeit7083deg: this.formatTime(satTimes.tzeit7083deg),
          tzeit85deg: this.formatTime(satTimes.tzeit85deg),
          tzeit42min: this.formatTime(satTimes.tzeit42min),
          tzeit50min: this.formatTime(satTimes.tzeit50min),
          tzeit72min: this.formatTime(satTimes.tzeit72min),
          havdalah: this.formatTime(havdalahItem?.date),
          netzHachama: this.formatTimeWithSeconds(satTimes.sunrise || satTimes.netzHachama),
          plagHamincha: this.formatTime(satTimes.plagHaMincha),
          minchaGedola: this.formatTime(satTimes.minchaGedola),
          minchaKetana: this.formatTime(satTimes.minchaKetana),
          chatzot: this.formatTime(satTimes.chatzot),
          alotHaShachar: this.formatTime(satTimes.alotHaShachar),
          chatzotNight: this.formatTime(satTimes.chatzotNight),
        }
      };

    } catch (error) {
      console.error('Error fetching Shabbat times:', error);
      throw error;
    }
  }

  // --- 4. תאריך עברי (Hebrew Date) ---
  async fetchHebrewDate(date = new Date()) {
    try {
      const dateStr = this.formatDateForApi(date);
      const response = await fetch(`https://www.hebcal.com/converter?cfg=json&date=${dateStr}&g2h=1&strict=1`);

      if (!response.ok) throw new Error('Hebrew date API failed');

      const data = await response.json();

      // עיבוד התאריך העברי לפורמט קצר יותר (ללא שנה)
      let hebrewDateShort = '';
      if (data.heDateParts) {
        const day = data.heDateParts.d;
        const month = data.heDateParts.m;

        // הסרת ניקוד מהחודש
        const monthWithoutNikud = month
          .replace(/[\u0591-\u05C7]/g, '') // הסרת ניקוד
          .replace(/ְ|ֱ|ֲ|ֳ|ִ|ֵ|ֶ|ַ|ָ|ֹ|ֺ|ֻ|ּ|ֽ|ֿ|ׁ|ׂ/g, ''); // הסרת ניקוד נוסף

        hebrewDateShort = `${day} ${monthWithoutNikud}`;
      }

      // Hebcal converter מחזיר hm כשם חודש באנגלית ("Nisan") — ממירים למספר
      const MONTH_TO_NUM = {
        Nisan: 1, Iyyar: 2, Sivan: 3, Tamuz: 4, Av: 5, Elul: 6,
        Tishrei: 7, Cheshvan: 8, Kislev: 9, Tevet: 10, "Sh'vat": 11, Shvat: 11,
        Adar: 12, 'Adar I': 12, 'Adar II': 13,
      };

      return {
        hebrew: data.hebrew,
        hebrewShort: hebrewDateShort,
        heDateParts: data.heDateParts,
        hy: data.hy,
        hd: data.hd,                              // יום עברי נומרי (1-30)
        hm: MONTH_TO_NUM[data.hm] ?? data.hm,     // חודש עברי נומרי (1=ניסן, 7=תשרי)
      };
    } catch (error) {
      console.error('Error fetching Hebrew date:', error);
      return {
        hebrew: 'תאריך עברי לא זמין',
        hebrewShort: '',
        heDateParts: null
      };
    }
  }

  // --- 5. זמנים לתאריך ספציפי (Zmanim for specific date) ---
  async fetchZmanimForDate(coordinates, dateStr) {
    try {
      if (!coordinates || !coordinates.lat || !coordinates.lng) {
        throw new Error('חסרים קואורדינטות');
      }
      const { lat, lng } = coordinates;
      const tzid = this._getTzid(coordinates);
      const response = await fetch(
        `https://www.hebcal.com/zmanim?cfg=json&geo=pos&sec=1&latitude=${lat}&longitude=${lng}&tzid=${tzid}&date=${dateStr}`
      );
      if (!response.ok) throw new Error('Zmanim API failed');
      const data = await response.json();
      const times = data.times;

      // סוף זמן אכילת חמץ (גר"א) = שעה 4 = זהה לסוף זמן תפילה (גר"א)
      const sofZmanAchilatChametz = times.sofZmanTfilla;

      // סוף זמן ביעור חמץ (גר"א) = שעה 5 = אמצע בין סוף תפילה לחצות
      let sofZmanBiurChametz = null;
      if (times.sofZmanTfilla && times.chatzot) {
        sofZmanBiurChametz = new Date(
          (new Date(times.sofZmanTfilla).getTime() + new Date(times.chatzot).getTime()) / 2
        );
      }

      // סוף זמן אכילת חמץ (מג"א) = שעה 4 = זהה לסוף זמן תפילה (מג"א 16.1°)
      const sofZmanAchilatChametzMGA = times.sofZmanTfillaMGA16Point1;

      // סוף זמן ביעור חמץ (מג"א) = שעה 5 = אמצע בין סוף תפילה מג"א לחצות
      let sofZmanBiurChametzMGA = null;
      if (times.sofZmanTfillaMGA16Point1 && times.chatzot) {
        sofZmanBiurChametzMGA = new Date(
          (new Date(times.sofZmanTfillaMGA16Point1).getTime() + new Date(times.chatzot).getTime()) / 2
        );
      }

      // passthrough של כל מפתחות ה-API (כולל בעל התניא, משיכיר וכו') כ-HH:MM,
      // כדי שכל זמן שנבחר באשף (MoadZmanimPerDayStep) יהיה פתיר גם ב-runtime.
      const passthrough = {};
      Object.entries(times).forEach(([k, v]) => { passthrough[k] = this.formatTime(v); });

      return {
        ...passthrough,
        // נרמול שמות/שיטות פנימיים (חייבים לגבור על ה-passthrough)
        netzHachama: this.formatTimeWithSeconds(times.sunrise || times.netzHachama),
        plagHamincha: this.formatTime(times.plagHaMincha),
        sofZmanShmaMGA: this.formatTime(times.sofZmanShmaMGA19Point8),
        sofZmanTfillaMGA: this.formatTime(times.sofZmanTfillaMGA19Point8),
        // זמני חמץ מחושבים (לא מוחזרים ישירות מ-Hebcal)
        sofZmanBiurChametz: this.formatTime(sofZmanBiurChametz),
        sofZmanAchilatChametz: this.formatTime(sofZmanAchilatChametz),
        sofZmanBiurChametzMGA: this.formatTime(sofZmanBiurChametzMGA),
        sofZmanAchilatChametzMGA: this.formatTime(sofZmanAchilatChametzMGA),
      };
    } catch (error) {
      console.error('Error fetching zmanim for date:', error);
      throw error;
    }
  }

  // --- 6. שליפת אירועי לוח שנה ביום ספציפי (הדלקת נרות / הבדלה) ---
  /**
   * שולף הדלקת נרות והבדלה/סיום צום עבור תאריך לועזי ספציפי.
   * מחזיר { candleLighting: 'HH:MM', havdalah: 'HH:MM' } — ריק אם אין אירועים ביום זה.
   * הדלקת נרות מופיעה בערב יום טוב / ערב יום כיפור (= תחילת צום).
   * הבדלה מופיעה ביום טוב / יום כיפור עצמו (= סיום צום).
   */
  async fetchCalendarEventsForDate(coordinates, dateStr) {
    try {
      const { lat, lng } = coordinates;
      const tzid = this._getTzid(coordinates);
      const response = await fetch(
        `https://www.hebcal.com/hebcal?v=1&cfg=json&maj=on&mf=on&c=on&geo=pos` +
        `&latitude=${lat}&longitude=${lng}&tzid=${tzid}&start=${dateStr}&end=${dateStr}`
      );
      if (!response.ok) return {};
      const data = await response.json();
      const result = {};
      (data.items || []).forEach(item => {
        if (!item.date || !item.date.includes('T')) return;
        if (item.date.substring(0, 10) !== dateStr) return;
        if (item.category === 'candles')  result.candleLighting = this.formatTime(item.date);
        else if (item.category === 'havdalah') result.havdalah = this.formatTime(item.date);
        else if (item.category === 'zmanim' && item.subcat === 'fast') {
          if (item.title?.toLowerCase().includes('begins')) result.fastBegins = this.formatTime(item.date);
          else result.fastEnds = this.formatTime(item.date);
        }
      });
      return result;
    } catch {
      return {};
    }
  }

  // --- 7. המרת תאריך עברי ללועזי ---
  async convertHebrewToGregorian(hebrewDay, hebrewMonth, hebrewYear) {
    // Hebcal API מקבל שמות חודשים בלבד (Nisan, Tishrei...) — לא מספרים
    // ממיר מספר לשם אם צריך
    const MONTH_NUM_TO_NAME = {
      1: 'Nisan', 2: 'Iyar', 3: 'Sivan', 4: 'Tamuz', 5: 'Av', 6: 'Elul',
      7: 'Tishrei', 8: 'Cheshvan', 9: 'Kislev', 10: 'Tevet', 11: 'Shvat',
      12: 'Adar1', 13: 'Adar2',
    };
    const monthParam = typeof hebrewMonth === 'number'
      ? (MONTH_NUM_TO_NAME[hebrewMonth] || hebrewMonth)
      : hebrewMonth;
    try {
      const response = await fetch(
        `https://www.hebcal.com/converter?cfg=json&hy=${hebrewYear}&hm=${monthParam}&hd=${hebrewDay}&h2g=1`
      );
      if (!response.ok) throw new Error('Hebrew converter API failed');
      const data = await response.json();
      const gy = data.gy;
      const gm = String(data.gm).padStart(2, '0');
      const gd = String(data.gd).padStart(2, '0');
      return `${gy}-${gm}-${gd}`;
    } catch (error) {
      console.error('Error converting Hebrew date:', error);
      throw error;
    }
  }

  // --- פונקציות עזר (Helpers) ---

  // חישוב תאריכי סוף שבוע (שישי ושבת)
  getWeekendDates() {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 6 = שבת

    const friday = new Date(today);

    if (dayOfWeek === 6) {
      // אם היום שבת - שישי היה אתמול
      friday.setDate(today.getDate() - 1);
    } else {
      // אם יום חול - נחשב את השישי הקרוב
      const daysUntilFriday = 5 - dayOfWeek;
      friday.setDate(today.getDate() + daysUntilFriday);
    }

    // שבת = תמיד יום אחרי שישי (נגזר מ-friday כדי להימנע מבאג חציית חודש)
    const saturday = new Date(friday);
    saturday.setDate(friday.getDate() + 1);

    return { friday, saturday };
  }

  // המרת תאריך לפורמט YYYY-MM-DD
  formatDateForApi(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // פירמוט שעה (HH:MM) — תמיד לפי שעון ישראל
  formatTime(dateString) {
    if (!dateString) return '--:--';
    try {
      // עבור מחרוזות ISO מה-API (כוללות T וה-offset) — חלץ ישירות מהמחרוזת
      // הזמן במחרוזת הוא כבר בשעון ישראל (כי שלחנו tzid=Asia/Jerusalem)
      if (typeof dateString === 'string' && dateString.includes('T')) {
        const m = dateString.match(/T(\d{2}):(\d{2})/);
        if (m) return `${m[1]}:${m[2]}`;
      }
      // עבור אובייקטי Date (זמנים מחושבים כגון חמץ) — המר לשעון ישראל
      const date = dateString instanceof Date ? dateString : new Date(dateString);
      if (isNaN(date.getTime())) return '--:--';
      const parts = new Intl.DateTimeFormat('en-GB', {
        hour: '2-digit', minute: '2-digit',
        hour12: false, timeZone: 'Asia/Jerusalem'
      }).formatToParts(date);
      const h = parts.find(p => p.type === 'hour')?.value || '';
      const min = parts.find(p => p.type === 'minute')?.value || '';
      return h && min ? `${h.padStart(2, '0')}:${min.padStart(2, '0')}` : '--:--';
    } catch (e) {
      return '--:--';
    }
  }

  // פירמוט שעה עם שניות (HH:MM:SS) — לנץ החמה בלבד
  formatTimeWithSeconds(dateString) {
    if (!dateString) return '--:--';
    try {
      if (typeof dateString === 'string' && dateString.includes('T')) {
        const m = dateString.match(/T(\d{2}):(\d{2}):(\d{2})/);
        if (m) return `${m[1]}:${m[2]}:${m[3]}`;
      }
      const date = dateString instanceof Date ? dateString : new Date(dateString);
      if (isNaN(date.getTime())) return '--:--';
      const parts = new Intl.DateTimeFormat('en-GB', {
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false, timeZone: 'Asia/Jerusalem'
      }).formatToParts(date);
      const h = parts.find(p => p.type === 'hour')?.value || '';
      const min = parts.find(p => p.type === 'minute')?.value || '';
      const sec = parts.find(p => p.type === 'second')?.value || '';
      return h && min && sec ? `${h.padStart(2, '0')}:${min.padStart(2, '0')}:${sec.padStart(2, '0')}` : '--:--';
    } catch (e) {
      return '--:--';
    }
  }


}

const apiService = new ApiService();
export default apiService;
